import { useState } from "react";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash } from "@phosphor-icons/react";

/**
 * Modifier group editor used by the Menu → Modifiers tab. Standalone so
 * the Add-Group and Edit-Group dialogs both mount a fresh form scoped to
 * one template.
 */
const ModifierTemplateForm = ({ initial, onSave, onCancel, editing, setEditing }) => {
  const [form, setForm] = useState(initial || { group_name: "", required: false, min_select: 0, max_select: 0, options: [{ name: "", price: 0 }] });

  const updateForm = (updates) => {
    const next = { ...form, ...updates };
    setForm(next);
    if (setEditing) setEditing(prev => ({ ...prev, ...updates }));
  };

  const updateOption = (idx, field, value) => {
    const opts = [...form.options];
    opts[idx] = { ...opts[idx], [field]: field === "price" ? (parseFloat(value) || 0) : value };
    updateForm({ options: opts });
  };

  const addOption = () => updateForm({ options: [...form.options, { name: "", price: 0 }] });
  const removeOption = (idx) => updateForm({ options: form.options.filter((_, i) => i !== idx) });

  return (
    <div className="space-y-4 py-2">
      <div className="space-y-2">
        <Label>Group Name</Label>
        <Input placeholder="e.g. Size, Toppings, Cooking Temp" value={form.group_name} onChange={(e) => updateForm({ group_name: e.target.value })} data-testid="tmpl-group-name" />
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div className="flex items-center gap-2 pt-6">
          <input type="checkbox" id="tmpl-req" checked={form.required} onChange={(e) => updateForm({ required: e.target.checked })} />
          <Label htmlFor="tmpl-req" className="text-sm">Required</Label>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Min selections</Label>
          <Input
            type="number"
            min={0}
            value={form.min_select ?? 0}
            onChange={(e) => updateForm({ min_select: Math.max(0, parseInt(e.target.value) || 0) })}
            className="h-8"
            data-testid="tmpl-min-select"
            title="Minimum options the customer must pick. Leave 0 to only rely on the Required checkbox."
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Max selections (0 = ∞)</Label>
          <Input
            type="number"
            min={0}
            value={form.max_select}
            onChange={(e) => updateForm({ max_select: parseInt(e.target.value) || 0 })}
            className="h-8"
            data-testid="tmpl-max-select"
          />
        </div>
      </div>
      {form.min_select > 0 && form.max_select > 0 && form.min_select > form.max_select && (
        <p className="text-xs text-destructive" data-testid="tmpl-min-max-warn">
          Min selections ({form.min_select}) cannot exceed max selections ({form.max_select}).
        </p>
      )}

      <div>
        <div className="flex items-center justify-between mb-2">
          <Label className="text-sm font-medium">Options & Pricing</Label>
          <Button variant="outline" size="sm" onClick={addOption} className="text-xs h-7" data-testid="tmpl-add-option"><Plus size={12} className="mr-1" /> Option</Button>
        </div>
        <div className="space-y-2">
          {form.options.map((opt, idx) => (
            <div key={idx} className="flex items-center gap-2" data-testid={`tmpl-opt-${idx}`}>
              <Input placeholder="Option name" value={opt.name} onChange={(e) => updateOption(idx, "name", e.target.value)} className="flex-1 h-9" />
              <div className="flex items-center gap-1">
                <span className="text-xs text-muted-foreground">$</span>
                <Input type="number" step="0.01" min="0" placeholder="0.00" value={opt.price || ""} onChange={(e) => updateOption(idx, "price", e.target.value)} className="w-24 h-9 font-mono text-sm" />
              </div>
              {form.options.length > 1 && (
                <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive flex-shrink-0" onClick={() => removeOption(idx)}><Trash size={14} /></Button>
              )}
            </div>
          ))}
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button
          onClick={() => onSave(form)}
          disabled={
            !form.group_name
            || form.options.length === 0
            || (form.min_select > 0 && form.max_select > 0 && form.min_select > form.max_select)
          }
          data-testid="save-modifier-btn"
        >
          {initial ? "Save Changes" : "Create Group"}
        </Button>
      </DialogFooter>
    </div>
  );
};

export default ModifierTemplateForm;
